import MessageBus from '@gaming/mtt-postal-kit';
import get, { noop } from 'src/utils/lodash-alternatives';
import { v4 as uuid } from 'uuid';
import { listen } from '../utils/message-bus';
import { PORTS_UNSUBSCRIBE, PORTS_SUBSCRIBE, PORTS_INVOKE } from './topics';

const validateInvocation = (port, portId, portResource) => {
  if (!port) return `Port with id "${portId}" was not found`;
  if (!port.invoke)
    return `Port with id "${portId}" has no invocable operations`;
  const invocable = get(port.invoke, portResource);
  if (!invocable)
    return `Operation "${portResource.join(
      '.'
    )}" is not supported on port with id: "${portId}"`;

  return null;
};

const validateSubscription = (port, portId, portResource) => {
  if (!port) return `Port with id "${portId}" was not found`;
  if (!port.on) return `Port with id "${portId}" has no resources to listen on`;

  const listenable = get(port.on, portResource);

  if (!listenable)
    return `Operation "${portResource.join(
      '.'
    )}" is not supported on port with id: "${portId}"`;

  return null;
};

const createPortsFacade = portsInventory => {
  const unsubscribersMap = new Map();

  const invocationSubscription = listen({
    topic: PORTS_INVOKE,
    callback({ data: { resource, args } }, replyOK, replyError) {
      const [portId, ...portResource] = resource.split('.');
      portsInventory.get(portId).then(port => {
        const invocationError = validateInvocation(port, portId, portResource);

        if (invocationError) {
          return replyError(invocationError, { resource });
        }

        const invocable = get(port.invoke, portResource);

        return Promise.resolve(invocable(args))
          .then(output => replyOK(output, { resource }))
          .catch(error =>
            replyError(error instanceof Error ? error.message : error, {
              resource,
            })
          );
      });
    },
  });

  const listenerSubscription = listen({
    topic: PORTS_SUBSCRIBE,
    callback({ data: { resource, args } }, replyOK, replyError) {
      const [portId, ...portResource] = resource.split('.');

      portsInventory.get(portId).then(port => {
        const subsctioptionError = validateSubscription(
          port,
          portId,
          portResource
        );
        if (subsctioptionError) {
          return replyError(subsctioptionError, { resource });
        }

        const listenable = get(port.on, portResource);
        const listenerTopic = uuid();
        const unsubscribe = listenable(
          output =>
            MessageBus.publish({
              topic: listenerTopic,
              data: output,
            }),
          args
        );

        unsubscribersMap.set(listenerTopic, unsubscribe);

        return replyOK({ listenerTopic }, { resource });
      });
    },
  });

  const listenerRemovalSubscription = listen({
    topic: PORTS_UNSUBSCRIBE,
    callback({ data: { listenerTopic, resource } }, replyOK) {
      const unsubscribe = unsubscribersMap.get(listenerTopic) || noop;
      unsubscribe();

      return replyOK({ listenerTopic }, { resource });
    },
  });

  return () => {
    MessageBus.unsubscribe(invocationSubscription);
    MessageBus.unsubscribe(listenerSubscription);
    MessageBus.unsubscribe(listenerRemovalSubscription);
    unsubscribersMap.forEach(unsubscribe => unsubscribe());
  };
};

export default createPortsFacade;
